/**
 * TAN-9584 — Conversión a PDF de ficheros ofimáticos en files-storage.
 *
 * Al subir un binario ofimático (multipart con `file` + `token`) hay que enviar dos campos
 * extra para que el backend genere además el PDF: `convert_to` (siempre `pdf`) y `path`, que
 * debe coincidir byte a byte con el nombre del campo `file` porque el conversor localiza el
 * binario por ese nombre. Para el resto de ficheros el payload queda intacto.
 *
 * La detección es siempre por extensión, nunca por MIME: el navegador reporta MIME vacío o
 * `application/octet-stream` para buena parte de estos formatos.
 *
 * Gemelo del módulo de Lemonade (`src/common/util/uploadUtils/officeConversion.js`, TAN-9449),
 * que cubre las subidas que hace la propia librería. Los dos deben comportarse igual.
 *
 * Punto de entrada: {@link appendUploadFile}.
 */

/**
 * Extensiones que se suben pidiendo la conversión. Es la lista canónica compartida con
 * Lemonade (`officeFileExtensions`): añadir o quitar una implica hacerlo en los dos repos.
 *
 * files-storage convierte más variantes (`dot`, `docm`, `pptm`…), y en Frontoffice además son
 * seleccionables (el `accept` sale de `CTTY_12 'OFFICE'` en `core/variables/typesFormat.js`),
 * pero no se piden: prima tener una sola lista. Al revés, `odt`/`ods`/`odp` aún no están en
 * ese catálogo, así que hoy el stream las rechaza antes de subirlas; se mantienen por paridad.
 */
export const OFFICE_FILE_EXTENSIONS = [
  // Word
  'doc',
  'docx',
  // PowerPoint
  'ppt',
  'pptx',
  // Excel
  'xls',
  'xlsx',
  // OpenDocument
  'odt',
  'ods',
  'odp',
] as const;

export type OfficeFileExtension = (typeof OFFICE_FILE_EXTENSIONS)[number];

export const OFFICE_FILES_CONVERT_TO = 'pdf';

/**
 * Caracteres que se sustituyen por `_` en el nombre de subida de un fichero ofimático.
 *
 * Los tres primeros los escapa el navegador en el `filename` del campo `file` (`"`→`%22`,
 * CR→`%0D`, LF→`%0A`) pero no en el valor del campo `path`, así que files-storage recibiría
 * dos nombres distintos. El `%` va detrás para que escapar y desescapar sea la identidad: sin
 * él, un `%22` literal sería indistinguible de una comilla escapada. Las barras evitan que
 * `path` se lea como una ruta.
 */
const UNSAFE_UPLOAD_FILE_NAME_CHARS = /["\r\n%\\/]/g;

/** Extensión en minúsculas y sin punto, o `''` si el nombre no tiene extensión. */
export function getFileExtension(fileName: string | undefined): string {
  if (!fileName) return '';

  const lastDotIndex = fileName.lastIndexOf('.');
  // Sin punto, o punto inicial (`.gitignore`), o punto final (`file.`): no hay extensión.
  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) return '';

  // El `trim()` ignora los blancos finales del nombre, que no forman parte de la extensión.
  return fileName
    .slice(lastDotIndex + 1)
    .trim()
    .toLowerCase();
}

/** Indica si el fichero es un documento ofimático convertible a PDF. */
export function isOfficeFileName(fileName: string | undefined): boolean {
  return (OFFICE_FILE_EXTENSIONS as readonly string[]).includes(getFileExtension(fileName));
}

/**
 * Nombre con el que hay que subir el fichero: saneado si es ofimático (ver
 * {@link UNSAFE_UPLOAD_FILE_NAME_CHARS}), tal cual si no, para no alterar el resto de subidas.
 *
 * Los blancos finales se recortan ANTES de sanear: si no, un `'Informe.docx\n'` se subiría
 * como `'Informe.docx_'`, sin extensión reconocible.
 */
export function getOfficeUploadFileName(fileName: string): string {
  if (!isOfficeFileName(fileName)) return fileName;

  return fileName.trimEnd().replaceAll(UNSAFE_UPLOAD_FILE_NAME_CHARS, '_');
}

/**
 * Añade `convert_to` y `path` si el fichero es ofimático. Hay que invocarlo con el MISMO nombre
 * usado en `formData.append('file', file, uploadFileName)`; prefiere {@link appendUploadFile},
 * que hace ambos appends y evita ese acoplamiento.
 *
 * `isOfficeFile` se recibe en vez de recalcularse sobre `uploadFileName`, que ya viene saneado:
 * reevaluarlo permitiría que la decisión de convertir discrepase de la que tomó el saneado.
 */
export function appendOfficeConversionParams(formData: FormData, uploadFileName: string, isOfficeFile: boolean): FormData {
  if (!isOfficeFile) return formData;

  formData.append('convert_to', OFFICE_FILES_CONVERT_TO);
  formData.append('path', uploadFileName);

  return formData;
}

/**
 * Añade el campo `file` y, si procede, los campos de conversión. Es la API que deberían usar los
 * call sites: al decidir aquí el nombre de subida, `path` no puede divergir del binario.
 *
 * El fichero ofimático puede acabar almacenado con un nombre ligeramente distinto al que eligió
 * el usuario. No afecta a lo que ve: el nombre visible sale del registro que crea el ticket.
 *
 * Con `fileName` a `undefined` se delega en el nombre por defecto del binario y no se convierte.
 */
export function appendUploadFile(formData: FormData, file: Blob, fileName: string | undefined): FormData {
  if (fileName === undefined) {
    formData.append('file', file);
    return formData;
  }

  // La condición se evalúa sobre el nombre previo al saneado, ver appendOfficeConversionParams.
  const isOfficeFile = isOfficeFileName(fileName);
  const uploadFileName = getOfficeUploadFileName(fileName);

  formData.append('file', file, uploadFileName);

  return appendOfficeConversionParams(formData, uploadFileName, isOfficeFile);
}
